import { Callout } from "@bednbody/ui/components/callout";
import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Textarea } from "@bednbody/ui/components/textarea";
import { ArrowsOutIcon, EyeIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import type { KycImage } from "../kyc.hooks";
import { formatBytes, type KycQueueRow } from "../kyc-queue";
import { DocumentViewer } from "./DocumentViewer";

interface KycReviewDialogProps {
  row: KycQueueRow | null;
  imageUrls: Record<KycImage, string | null>;
  loadingImage: KycImage | null;
  isDeciding: boolean;
  errorMessage: string | null;
  onOpenImage: (image: KycImage) => void;
  onClose: () => void;
  onSubmit: (input: { decision: "verified" | "rejected"; reason: string }) => void;
}

const IMAGE_LABEL: Record<KycImage, string> = {
  document: "ID",
  selfie: "Selfie",
};

/**
 * One of the two images, opened on request. Each open mints its own signed URL
 * and writes its own `kyc_document_viewed` row naming the image.
 */
function KycImagePanel({
  image,
  clientName,
  url,
  loading,
  disabled,
  onOpen,
  onEnlarge,
}: {
  image: KycImage;
  clientName: string;
  url: string | null;
  loading: boolean;
  disabled: boolean;
  onOpen: () => void;
  onEnlarge: () => void;
}) {
  const label = IMAGE_LABEL[image];
  return (
    <section aria-label={label} className="space-y-2">
      <h3 className="text-sm font-medium text-foreground">{label}</h3>
      {url ? (
        // BL-42 — the whole image, clickable, so a licence number can be read.
        <button
          aria-label={`Open the ${label.toLowerCase()} larger`}
          className="group relative block w-full overflow-hidden rounded-lg border border-border bg-muted/40 outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onEnlarge}
          type="button"
        >
          <img
            alt={`${label} submitted by ${clientName}`}
            className="max-h-[28rem] w-full object-contain"
            src={url}
          />
          <span className="pointer-events-none absolute right-2 top-2 flex items-center gap-1.5 rounded-md bg-foreground/80 px-2 py-1 text-xs font-medium text-background">
            <ArrowsOutIcon weight="bold" />
            Enlarge
          </span>
        </button>
      ) : (
        <Button
          className="w-full"
          disabled={disabled || loading}
          onClick={onOpen}
          variant="outline"
        >
          <EyeIcon weight="fill" />
          {loading ? "Opening…" : `Open the ${label.toLowerCase()}`}
        </Button>
      )}
    </section>
  );
}

/**
 * The KYC decision — the ID and the selfie side by side (BL-43), so the
 * reviewer compares the face on the card with the face in the photo.
 *
 * THE IMAGES ARE NOT LOADED WITH THE DIALOG, deliberately. Opening one is a
 * separate, explicit click because each mints a signed URL and writes a
 * `kyc_document_viewed` audit row naming the reviewer. Auto-loading would put a
 * look in the log for every dialog anyone opened, including the ones opened by
 * accident, and the log's value is that it means someone actually looked.
 *
 * Two things this says out loud rather than implying:
 *  1. **Who may review is undefined.** 0003 already grants hr and admin a
 *     table-wide UPDATE on profiles, so the temptation is to read that as
 *     permission; it is a capability, not an approved authority (OQ-81).
 *  2. **The reason never reaches the client.** It is withheld by column grant,
 *     and what a rejected client is told is undecided (OQ-82) — so a reviewer
 *     writing "blurry, retake in daylight" should not expect them to see it.
 */
export function KycReviewDialog({
  row,
  imageUrls,
  loadingImage,
  isDeciding,
  errorMessage,
  onOpenImage,
  onClose,
  onSubmit,
}: KycReviewDialogProps) {
  const [reason, setReason] = useState("");
  const [viewing, setViewing] = useState<KycImage | null>(null);

  // A reason typed against one client must never travel to the next.
  useEffect(() => {
    setReason("");
  }, [row?.id]);

  const purged = row?.purgedAt != null;
  const clientName = row?.clientName ?? "this client";
  const trimmedReason = reason.trim();
  // The server requires a reason on rejection (kycDecisionSchema) — mirrored
  // here so the reviewer learns it before the round trip, not after.
  const canReject = trimmedReason.length >= 3 && !isDeciding && !purged;
  const canApprove = !isDeciding && !purged;

  return (
    <>
      <Dialog
        open={row !== null}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Review identity</DialogTitle>
            <DialogDescription>
              {row
                ? `${row.clientName}${row.clientPhone ? ` · ${row.clientPhone}` : ""} · declared "${row.declaredDocumentType}" · ${formatBytes(row.byteSize)}`
                : null}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {purged ? (
              <div className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning/5 p-3">
                <WarningCircleIcon className="mt-0.5 size-5 shrink-0 text-warning" weight="fill" />
                <p className="text-sm">
                  These images were deleted under the retention purge. The submission record
                  survives, the images do not — there is nothing left to review.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid gap-4 sm:grid-cols-2">
                  <KycImagePanel
                    clientName={clientName}
                    disabled={false}
                    image="document"
                    loading={loadingImage === "document"}
                    onEnlarge={() => setViewing("document")}
                    onOpen={() => onOpenImage("document")}
                    url={imageUrls.document}
                  />
                  {row?.hasSelfie ? (
                    <KycImagePanel
                      clientName={clientName}
                      disabled={false}
                      image="selfie"
                      loading={loadingImage === "selfie"}
                      onEnlarge={() => setViewing("selfie")}
                      onOpen={() => onOpenImage("selfie")}
                      url={imageUrls.selfie}
                    />
                  ) : (
                    <section aria-label="Selfie" className="space-y-2">
                      <h3 className="text-sm font-medium text-foreground">Selfie</h3>
                      <Callout tone="warning">
                        No selfie. This was sent before the app asked for one, so there is no face
                        to compare with the ID.
                      </Callout>
                    </section>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Loaded on request, not automatically: every open writes an audit row naming you. A
                  link expires in about a minute — open it again if the image stops loading.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="kyc-reason" className="block text-sm font-medium">
                Reviewer note{" "}
                <span className="font-normal text-muted-foreground">(required to reject)</span>
              </label>
              <Textarea
                id="kyc-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="What did you see, and why this decision?"
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground">
                Internal only — the client never sees this text. They are told the decision and
                nothing else.
              </p>
            </div>

            <Callout tone="warning">
              You are deciding as Superadmin because who may approve KYC has not been assigned.
              Every decision is written to the audit log, and approving unlocks cash-on-arrival
              bookings for this client.
            </Callout>

            {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isDeciding}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!canReject}
              onClick={() => onSubmit({ decision: "rejected", reason: trimmedReason })}
            >
              Reject
            </Button>
            <Button
              disabled={!canApprove}
              onClick={() => onSubmit({ decision: "verified", reason: trimmedReason })}
            >
              {isDeciding ? "Recording…" : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* A sibling, not a child: a dialog nested inside another dialog's root
          inherits its open state and its focus trap. */}
      <DocumentViewer
        alt={`${viewing ? IMAGE_LABEL[viewing] : "Image"} submitted by ${clientName}`}
        onOpenChange={(open) => {
          if (!open) setViewing(null);
        }}
        open={viewing !== null}
        src={viewing ? imageUrls[viewing] : null}
        title={`${viewing ? IMAGE_LABEL[viewing] : "Image"} · ${row?.clientName ?? "client"}`}
      />
    </>
  );
}
